import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { LucideAngularModule, ChevronDown } from 'lucide-angular';
import { NavDropdownComponent } from './nav-dropdown';
import { NavItem } from './nav-config';

@Component({
  selector: 'sh-nav-item',
  imports: [LucideAngularModule, NavDropdownComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'relative inline-flex',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
  template: `
    <button
      type="button"
      class="nav-tab"
      [class.nav-tab--active]="isActive()"
      (click)="onTabClick()"
      [attr.aria-expanded]="hasChildren() && dropdownOpen() ? 'true' : null"
      [attr.aria-haspopup]="hasChildren() ? 'menu' : null">
      <lucide-angular [img]="item().icon" [size]="15" class="nav-tab__icon"></lucide-angular>
      <span class="nav-tab__label">{{ item().label }}</span>
      @if (hasChildren()) {
        <lucide-angular
          [img]="chevronIcon"
          [size]="12"
          class="nav-tab__chevron"
          [class.nav-tab__chevron--open]="dropdownOpen()">
        </lucide-angular>
      }
    </button>

    @if (hasChildren() && dropdownOpen()) {
      <sh-nav-dropdown
        [items]="item().children!"
        (itemSelected)="onDropdownItemSelected($event)" />
    }
  `,
  styles: [`
    :host {
      position: relative;
      display: inline-flex;
    }

    .nav-tab {
      position: relative;
      z-index: 10;
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 6px 10px;
      border-radius: 9999px;
      border: none;
      background: none;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--color-text-primary);
      cursor: pointer;
      white-space: nowrap;
      transition: color 200ms ease;
      outline: none;
    }

    .nav-tab:hover {
      color: var(--color-text-on-brand);
    }

    .nav-tab:focus-visible {
      outline: 2px solid var(--color-brand-primary);
      outline-offset: 2px;
    }

    .nav-tab--active {
      color: var(--color-text-on-brand);
    }

    .nav-tab__icon {
      flex-shrink: 0;
      opacity: 0.75;
    }

    .nav-tab--active .nav-tab__icon {
      opacity: 1;
    }

    .nav-tab__label {
      line-height: 1;
    }

    .nav-tab__chevron {
      opacity: 0.5;
      transition: transform 200ms ease;
      flex-shrink: 0;
    }

    .nav-tab__chevron--open {
      transform: rotate(180deg);
    }

    @media (max-width: 1023px) {
      .nav-tab {
        padding: 5px 8px;
        font-size: 0.75rem;
        gap: 3px;
      }

      .nav-tab__label {
        /* Hide labels on smaller screens if needed */
      }
    }
  `],
})
export class NavItemComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly item = input.required<NavItem>();
  readonly isActive = input(false);
  readonly tabActivated = output<void>();

  protected readonly dropdownOpen = signal(false);
  protected readonly chevronIcon = ChevronDown;

  private leaveTimeout: ReturnType<typeof setTimeout> | null = null;

  protected hasChildren(): boolean {
    const children = this.item().children;
    return !!children && children.length > 0;
  }

  protected onTabClick(): void {
    this.tabActivated.emit();
    this.router.navigate([this.item().route]);
  }

  protected onMouseEnter(): void {
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout);
      this.leaveTimeout = null;
    }
    if (this.hasChildren()) {
      this.dropdownOpen.set(true);
    }
  }

  protected onMouseLeave(): void {
    if (this.hasChildren()) {
      this.leaveTimeout = setTimeout(() => {
        this.dropdownOpen.set(false);
        this.leaveTimeout = null;
      }, 150);
    }
  }

  protected onDropdownItemSelected(route: string): void {
    this.dropdownOpen.set(false);
    this.router.navigate([route]);
  }

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.leaveTimeout) {
        clearTimeout(this.leaveTimeout);
      }
    });
  }
}
